/**
 * identity.js
 *
 * 匿名識別トークンのクライアント実装（31_匿名識別とセッション.md 第2章・第7章、
 * _決定事項レジスタ.md 2.16 / 3.7 に準拠）。
 *
 * 3層（Cookie `xte_identity` / localStorage `xte:identity-token` / IndexedDB
 * `identities` ストア）に同一のトークン文字列を保持し、「最も新しい iat を持つ
 * 有効なトークン」を正として自己修復する（署名検証はサーバーのみが行う。
 * ここでのデコードはUIヒント表示専用で、改ざんされていても実害がない用途に限る。
 * 31 §1.3, §2.5）。
 *
 * 新規発行は「初回の書き込み操作の直前」にのみ行う（31 §1.2）。ページ閲覧だけの
 * ユーザーに対して無条件に識別子を発行しない。
 *
 * IMPLEMENTATION.md 第5章のJS規約に従う: ES2022 / named export / const優先 /
 * async-await / フレームワーク不使用。
 */

// api.js は import しない。循環参照になるだけでなく、無限再帰を起こすため。
//
//   api.js は書き込み時に identity.js の ensureIdentity() を呼んでトークンを得る
//     → その ensureIdentity() が api.post() で識別子を発行しようとする
//       → api.post() がまた ensureIdentity() を呼ぶ … と無限に続く
//
// このモジュールは識別子レイヤーの最下層であり、上位の HTTP 層に依存できない。
// 幸い `POST /identity` は nonce 不要な唯一のエンドポイント（32_REST_API設計 第3章）
// なので、素の fetch で完結できる。
import { dbGet, dbPut, dbDelete } from './db.js';

const LS_KEY = 'xte:identity-token';
const COOKIE_NAME = 'xte_identity'; // _決定事項レジスタ.md 3.7 の確定名（`xte_id` ではない）
const CONSENT_COOKIE_NAME = 'xte_consent';
const COOKIE_MAX_AGE_SEC = 400 * 24 * 60 * 60;
const IDB_STORE = 'identities';
const IDB_KEY = 'current';
const REST_PATH = '/xte/v1/identity';
const SCREEN_BUCKETS = ['xs', 'sm', 'md', 'lg', 'xl', '2xl'];

let currentToken = null;
let ensureInFlight = null;
const listeners = new Set();
const channel = 'BroadcastChannel' in window ? new BroadcastChannel( 'xte-identity' ) : null;

// ---------------------------------------------------------------------
// 自己完結の POST（api.js に依存しない理由は冒頭の import 部のコメントを参照）
// ---------------------------------------------------------------------

/**
 * 識別子エンドポイントへ POST する。
 *
 * api.js の `api.post()` と同じ呼び出し形にしてあるので、呼び出し側は変更不要。
 * ただしリトライ・重複排除・nonce 添付といった上位層の機能は持たない。
 * このエンドポイントは nonce 不要で、失敗しても呼び出し側が握りつぶせる設計のため
 * それで足りる。
 *
 * @param {string} path    `/xte/v1/identity`
 * @param {object} body    JSON ボディ
 * @param {{auth?: string}} [opts] `auth` があれば Authorization ヘッダに載せる
 * @returns {Promise<object>} レスポンスの JSON
 * @throws {Error} 通信失敗、または 2xx 以外
 */
async function postIdentity( path, body, opts = {} ) {
	const headers = { 'Content-Type': 'application/json' };
	if ( opts.auth ) {
		headers.Authorization = `Bearer ${ opts.auth }`;
	}

	const controller = new AbortController();
	const timer = setTimeout( () => controller.abort(), 15000 );

	try {
		const response = await fetch( `/wp-json${ path }`, {
			method: 'POST',
			headers,
			body: JSON.stringify( body ),
			credentials: 'same-origin',
			signal: controller.signal,
		} );

		const text = await response.text();
		let json = null;
		try {
			json = text ? JSON.parse( text ) : null;
		} catch {
			json = null;
		}

		if ( ! response.ok ) {
			const message = json && json.message ? json.message : `HTTP ${ response.status }`;
			const error = new Error( message );
			error.status = response.status;
			error.code = json && json.code ? json.code : 'xte_identity_request_failed';
			throw error;
		}

		return json || {};
	} finally {
		clearTimeout( timer );
	}
}

// ---------------------------------------------------------------------
// base64url / ペイロードのデコード（署名検証はしない。31 §1.3, §2.5）
// ---------------------------------------------------------------------

function base64urlDecode( str ) {
	const pad = '='.repeat( ( 4 - ( str.length % 4 ) ) % 4 );
	const b64 = ( str + pad ).replace( /-/g, '+' ).replace( /_/g, '/' );
	try {
		return decodeURIComponent(
			atob( b64 )
				.split( '' )
				.map( ( c ) => '%' + c.charCodeAt( 0 ).toString( 16 ).padStart( 2, '0' ) )
				.join( '' )
		);
	} catch {
		return '';
	}
}

function decodeTokenPayload( token ) {
	if ( typeof token !== 'string' ) {
		return null;
	}
	const parts = token.split( '.' );
	if ( parts.length !== 3 ) {
		return null;
	}
	try {
		return JSON.parse( base64urlDecode( parts[ 1 ] ) );
	} catch {
		return null;
	}
}

function isUsable( token ) {
	const payload = token ? decodeTokenPayload( token ) : null;
	return !! payload && typeof payload.exp === 'number' && payload.exp * 1000 > Date.now();
}

// ---------------------------------------------------------------------
// Cookie（非HttpOnly。JSによる3層自己修復のため。31 §2.2）
// ---------------------------------------------------------------------

function cookieRead( name ) {
	const match = document.cookie.match( new RegExp( '(?:^|; )' + name + '=([^;]*)' ) );
	return match ? decodeURIComponent( match[ 1 ] ) : null;
}

function cookieWrite( name, value, maxAgeSec ) {
	try {
		document.cookie = `${ name }=${ encodeURIComponent( value ) }; Max-Age=${ maxAgeSec }; Path=/; Secure; SameSite=Lax`;
		return true;
	} catch {
		// Cookieがブラウザ設定でブロックされている場合は静かに失敗する（31 §10）。
		return false;
	}
}

function cookieClear( name ) {
	try {
		document.cookie = `${ name }=; Max-Age=0; Path=/; Secure; SameSite=Lax`;
		return true;
	} catch {
		return false;
	}
}

// ---------------------------------------------------------------------
// localStorage（プライベートブラウジング等で例外を投げうる。必ずtry/catch。31 §2.3, §10）
// ---------------------------------------------------------------------

function lsRead() {
	try {
		return localStorage.getItem( LS_KEY );
	} catch {
		return null;
	}
}

function lsWrite( token ) {
	try {
		localStorage.setItem( LS_KEY, token );
		return true;
	} catch {
		return false;
	}
}

function lsClear() {
	try {
		localStorage.removeItem( LS_KEY );
		return true;
	} catch {
		return false;
	}
}

// ---------------------------------------------------------------------
// IndexedDB（db.js 経由。共有DB `xte` の `identities` ストア（keyPath: 'key'）に
// { key: 'current', token, updatedAt } の形で保持する。31 §2.4。
// このストアは db.js の STORE_DEFS に定義されている（未定義のストア名を渡すと
// db.js の assertStoreName() が例外を投げ、この層が丸ごと無効化される）。
// 失敗時は例外を握りつぶし、他レイヤーのみで継続する。ただし黙って消すと
// 「3層のつもりが実は2層」という状態に気付けないため、必ずログを1行出す。
// ---------------------------------------------------------------------

// db.js は接続を遅延オープンする設計のため、明示的な openDb() は存在しない。
// また書き込みは keyPath 方式の dbPut( store, value ) で、削除は dbDelete( store, key )。
// 当初この層は openDb()/dbSet() を前提に書かれていたが、db.js の実APIに合わせて修正した。

/**
 * IndexedDB層の失敗を記録する。動作は止めない（他の2層で継続する）が、
 * 開発時・RUM で原因を追えるようにする。
 *
 * main.js の reportError() と同じ流儀（console 出力 + `xte:client-error` の
 * CustomEvent）に合わせる。main.js を import すると識別子レイヤーが上位層に
 * 依存して循環するため、ここでは同じ detail 形状のイベントを直接発火する。
 *
 * @param {string} op   'read' | 'write' | 'clear'
 * @param {unknown} error
 */
function reportIdbFailure( op, error ) {
	const normalized = error instanceof Error ? error : new Error( String( error ) );
	const kind = `identity-idb-${ op }-failed`;

	console.warn( `[xte:${ kind }]`, normalized );

	try {
		document.dispatchEvent(
			new CustomEvent( 'xte:client-error', {
				detail: {
					kind,
					message: normalized.message,
					stack: normalized.stack ?? null,
					url: window.location.href,
					at: new Date().toISOString(),
					store: IDB_STORE,
				},
			} )
		);
	} catch {
		// ログ経路の失敗で本処理を巻き添えにしない。
	}
}

async function idbReadToken() {
	try {
		const record = await dbGet( IDB_STORE, IDB_KEY );
		return record && typeof record.token === 'string' ? record.token : null;
	} catch ( err ) {
		reportIdbFailure( 'read', err );
		return null;
	}
}

async function idbWriteToken( token ) {
	try {
		// ストアは keyPath: 'key' なので、キーは値の中に含めて渡す。
		await dbPut( IDB_STORE, { key: IDB_KEY, token, updatedAt: Date.now() } );
		return true;
	} catch ( err ) {
		reportIdbFailure( 'write', err );
		return false;
	}
}

async function idbClearToken() {
	try {
		// null を書き込むのではなくレコードごと消す。
		// 「値が null のレコードが残る」状態を作らないため。
		await dbDelete( IDB_STORE, IDB_KEY );
		return true;
	} catch ( err ) {
		reportIdbFailure( 'clear', err );
		return false;
	}
}

// ---------------------------------------------------------------------
// 3層の相互同期（31 §2.5）。ネットワーク通信を一切行わない純粋なローカル処理。
// ---------------------------------------------------------------------

async function reconcileLayers() {
	const fromCookie = cookieRead( COOKIE_NAME );
	const fromLs = lsRead();
	const fromIdb = await idbReadToken();

	const candidates = [ fromCookie, fromLs, fromIdb ].filter( isUsable );
	if ( candidates.length === 0 ) {
		return null;
	}

	let best = candidates[ 0 ];
	let bestIat = decodeTokenPayload( best ).iat;
	for ( const token of candidates.slice( 1 ) ) {
		const iat = decodeTokenPayload( token ).iat;
		if ( iat > bestIat ) {
			best = token;
			bestIat = iat;
		}
	}

	if ( fromCookie !== best ) cookieWrite( COOKIE_NAME, best, COOKIE_MAX_AGE_SEC );
	if ( fromLs !== best ) lsWrite( best );
	if ( fromIdb !== best ) await idbWriteToken( best );

	return best;
}

function screenBucket( width ) {
	if ( width < 360 ) return 'xs';
	if ( width < 480 ) return 'sm';
	if ( width < 768 ) return 'md';
	if ( width < 1024 ) return 'lg';
	if ( width < 1280 ) return 'xl';
	return '2xl';
}

function notify() {
	for ( const listener of listeners ) {
		listener( currentToken );
	}
}

function broadcast( message ) {
	channel?.postMessage( message );
}

async function persistToAllLayers( token ) {
	cookieWrite( COOKIE_NAME, token, COOKIE_MAX_AGE_SEC );
	lsWrite( token );
	await idbWriteToken( token );
}

async function clearAllLayers() {
	cookieClear( COOKIE_NAME );
	lsClear();
	await idbClearToken();
}

function adoptToken( token ) {
	currentToken = token;
	broadcast( { type: 'identity-updated', token } );
	notify();
	return persistToAllLayers( token );
}

// ---------------------------------------------------------------------
// 公開API
// ---------------------------------------------------------------------

/**
 * アプリ起動時に一度だけ呼ぶ。3層のローカル自己修復のみ行い、ネットワーク通信はしない。
 * 未発行・全消失時は null を返す（この時点では新規発行しない。31 §1.2, §2.6）。
 *
 * @returns {Promise<string|null>}
 */
export async function initIdentity() {
	currentToken = await reconcileLayers();

	if ( channel ) {
		channel.onmessage = ( event ) => {
			const data = event.data;
			if ( ! data ) {
				return;
			}
			if ( data.type === 'identity-reset' ) {
				currentToken = null;
				notify();
			} else if ( data.type === 'identity-updated' && data.token !== currentToken ) {
				currentToken = data.token;
				notify();
			}
		};
	}

	// storage イベント: BroadcastChannel未対応ブラウザ向けフォールバックを兼ねる、
	// 同一オリジンの別タブによる localStorage 書き換えへの追従（31 §10「複数タブ競合」）。
	const controller = new AbortController();
	window.addEventListener(
		'storage',
		( event ) => {
			if ( event.key !== LS_KEY ) {
				return;
			}
			if ( isUsable( event.newValue ) && event.newValue !== currentToken ) {
				currentToken = event.newValue;
				notify();
			} else if ( ! event.newValue ) {
				currentToken = null;
				notify();
			}
		},
		{ signal: controller.signal }
	);

	return currentToken;
}

/** 現在保持しているトークンを同期的に取得する（未発行なら null）。 */
export function getIdentity() {
	return currentToken;
}

/** 現在のトークンのペイロード（sub/iat/exp/v）をデコードする。UIヒント表示専用（署名未検証）。 */
export function getIdentityPayload() {
	return currentToken ? decodeTokenPayload( currentToken ) : null;
}

/** 識別子の変化（発行・更新・リセット・他タブでの変化）を購読する。 */
export function onIdentityChange( listener ) {
	listeners.add( listener );
	return () => listeners.delete( listener );
}

/**
 * 書き込み操作の直前に必ず呼ぶ。未発行なら新規発行、期限間近なら更新する（31 §1.2）。
 * 多重呼び出しは Web Locks（未対応時はモジュール内Promiseキャッシュ）で直列化し、
 * 二重発行を防ぐ（31 §10「複数タブ競合」）。
 *
 * @returns {Promise<string|null>}
 */
export async function ensureIdentity() {
	if ( currentToken && isUsable( currentToken ) ) {
		return currentToken;
	}
	if ( ensureInFlight ) {
		return ensureInFlight;
	}

	const run = async () => {
		const candidate = await reconcileLayers();
		if ( isUsable( candidate ) ) {
			currentToken = candidate;
		}

		let data;
		try {
			data = await postIdentity(
				REST_PATH,
				{
					action: 'ensure',
					want_issue: true,
					candidate_token: candidate || undefined,
					tz_offset_minutes: -new Date().getTimezoneOffset(),
					screen_bucket: screenBucket( window.innerWidth ),
				},
				currentToken ? { auth: currentToken } : {}
			);
		} catch {
			// ネットワーク不通時は既知のローカル候補があればそれを返し、無ければ null のまま
			// （縮退動作。呼び出し元の書き込み操作自体は別途エラー表示すること）。
			return currentToken;
		}

		if ( data && data.token ) {
			await adoptToken( data.token );
		}

		return currentToken;
	};

	ensureInFlight = ( async () => {
		if ( 'locks' in navigator ) {
			return navigator.locks.request( 'xte-identity-ensure', run );
		}
		return run();
	} )();

	try {
		return await ensureInFlight;
	} finally {
		ensureInFlight = null;
	}
}

/** 設定画面のリセット操作から呼ぶ。サーバー側の識別子を無効化し、3層すべてを消去する。 */
export async function resetIdentity() {
	if ( currentToken ) {
		try {
			await postIdentity( REST_PATH, { action: 'reset' }, { auth: currentToken } );
		} catch {
			// サーバー通知に失敗してもローカルのリセットは進める（ユーザー操作を裏切らない）。
		}
	}
	currentToken = null;
	await clearAllLayers();
	broadcast( { type: 'identity-reset' } );
	notify();
}

/**
 * 補助的な環境情報の保存可否を切り替える（31 §5.2「補助的な環境情報の保存を停止する」）。
 *
 * @param {'standard'|'minimal'} consent
 */
export async function setIdentityConsent( consent ) {
	cookieWrite( CONSENT_COOKIE_NAME, consent, COOKIE_MAX_AGE_SEC );
	const opts = currentToken ? { auth: currentToken } : {};
	return postIdentity( REST_PATH, { action: 'set_consent', consent }, opts );
}

/**
 * 引き継ぎコードを発行する（31 §4.1）。書き込み操作に相当するため ensureIdentity() を内包する。
 *
 * @param {'code'|'qr'|'email'} channel
 * @returns {Promise<{code:string,expires_at:string,qr_url:string}>}
 */
export async function createHandoff( channel = 'code' ) {
	const token = await ensureIdentity();
	if ( ! token ) {
		throw new Error( 'identity unavailable' );
	}
	return postIdentity( REST_PATH, { action: 'create_handoff', channel }, { auth: token } );
}

/**
 * 引き継ぎコードを使用し、引き継ぎ元の識別子をこの端末で採用する（31 §4.1, §4.2）。
 * 今の端末が既に別の識別子を持っていた場合、サーバー側でその由来は保持される
 * （`status=merged`。この端末上ではそのまま新しいトークンに置き換わる）。
 *
 * @param {string} code
 */
export async function redeemHandoff( code ) {
	const data = await postIdentity(
		REST_PATH,
		{ action: 'redeem_handoff', code },
		currentToken ? { auth: currentToken } : {}
	);
	if ( data && data.token ) {
		await adoptToken( data.token );
	}
	return data;
}

/**
 * 復旧候補の検索（31 §3.4）。補助シグナルの一致は「絞り込みのヒント」に過ぎず、
 * 単独では復元を完了させない。呼び出し元は必ず引き継ぎコード・メール確認等の
 * 正規の手段に誘導すること。
 */
export async function findRecoveryCandidates() {
	return postIdentity( REST_PATH, { action: 'find_candidates' }, {} );
}

/** 任意の復元用メール登録（31 §4.4）。未登録でも全機能利用可能であることが前提。 */
export async function registerRecoveryEmail( email ) {
	const token = await ensureIdentity();
	if ( ! token ) {
		throw new Error( 'identity unavailable' );
	}
	return postIdentity( REST_PATH, { action: 'register_email', email }, { auth: token } );
}

/**
 * 識別子をJSONファイルとしてエクスポートする（31 §4.3）。ファイル生成自体は
 * サーバー通信を伴わず、ローカルの3層から解決したトークンをそのまま使う。
 */
export async function exportIdentity() {
	const token = await reconcileLayers();
	if ( ! token ) {
		throw new Error( 'no identity to export' );
	}
	const payload = decodeTokenPayload( token );

	const blob = new Blob(
		[
			JSON.stringify(
				{
					format: 'xte-identity-export',
					version: 1,
					token,
					exportedAt: new Date().toISOString(),
					note: 'このファイルはあなたの匿名識別子です。第三者と共有すると、あなたのブックマーク・いいね履歴を他人が引き継げてしまいます。',
				},
				null,
				2
			),
		],
		{ type: 'application/json' }
	);

	const anchor = document.createElement( 'a' );
	anchor.href = URL.createObjectURL( blob );
	anchor.download = `x-twitter-ero-identity-${ ( payload?.sub || 'unknown' ).slice( 0, 8 ) }.json`;
	anchor.click();
	URL.revokeObjectURL( anchor.href );
}

/**
 * エクスポートされたJSONファイルを取り込む（31 §4.3）。クライアント単独では信じ込まず、
 * サーバーの `ensure` を経由して検証済みのトークンのみを採用する。
 *
 * @param {File} file
 */
export async function importIdentity( file ) {
	const text = await file.text();

	let data;
	try {
		data = JSON.parse( text );
	} catch {
		throw new Error( 'ファイルを読み取れませんでした' );
	}

	if ( data.format !== 'xte-identity-export' || ! isUsable( data.token ) ) {
		throw new Error( '無効な引き継ぎファイルです' );
	}

	const result = await postIdentity(
		REST_PATH,
		{
			action: 'ensure',
			want_issue: false,
			candidate_token: data.token,
		},
		{}
	);

	if ( ! result || ! result.token ) {
		throw new Error( '無効な引き継ぎファイルです' );
	}

	await adoptToken( result.token );

	return result;
}
